// =============================================================================
// Vendors — faithful port of POS frmVendorDashboard + IMS VendorManagement.
// List with search + add/edit (contact, terms, YTD purchases/paid, active).
// =============================================================================
import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { listVendors, createVendor, updateVendor } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button, FilterBar } from '../components/shared/ui'
import { currency } from '../lib/format'
import { useServerList } from '../lib/list'
import type { Vendor } from '../types'
import { toast } from 'react-toastify'

const PAGE_SIZE = 25
const blank = { name: '', vendor_code: '', phone: '', mobile: '', email: '', address: '', city: '', state: '', zip: '', website: '', terms: '', is_active: true }

export default function Vendors() {
  const [edit, setEdit] = useState<Vendor | 'new' | null>(null)
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort, reload } =
    useServerList<Vendor>(listVendors, { pageSize: PAGE_SIZE, defaultSort: { column: 'name', dir: 'asc' } })

  return (
    <div>
      <PageHeader title="Vendors" subtitle={`${count.toLocaleString()} total`}
        actions={<Button onClick={() => setEdit('new')}><Plus size={16} /> New Vendor</Button>} />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, code, email…" />
          <FilterBar onChange={setFilters} fields={[{ key: 'active', label: 'Active', column: 'is_active', type: 'boolean' }]} />
        </div>
        {loading ? <Spinner label="Loading vendors…" /> : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={v => setEdit(v)}
              columns={[
                { key: 'name', header: 'Name', render: v => <span className="font-medium text-gray-900">{v.name}</span>, sortKey: 'name' },
                { key: 'vendor_code', header: 'Code', render: v => v.vendor_code || '—', sortKey: 'vendor_code' },
                { key: 'phone', header: 'Phone', render: v => v.phone || '—', sortKey: 'phone' },
                { key: 'email', header: 'Email', render: v => v.email || '—', sortKey: 'email' },
                { key: 'terms', header: 'Terms', render: v => v.terms || '—', sortKey: 'terms' },
                { key: 'ytd_purchases', header: 'YTD Purchases', render: v => currency(v.ytd_purchases), className: 'text-right', sortKey: 'ytd_purchases' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
      {edit && <VendorForm vendor={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload() }} />}
    </div>
  )
}

function VendorForm({ vendor, onClose, onSaved }: { vendor: Vendor | null; onClose: () => void; onSaved: () => void }) {
  const init = vendor ? { ...blank, ...Object.fromEntries(Object.keys(blank).map(k => [k, (vendor as Record<string, unknown>)[k] ?? blank[k as keyof typeof blank]])) } : { ...blank }
  const [f, setF] = useState<typeof blank>(init as typeof blank)
  const [saving, setSaving] = useState(false)
  const set = (k: keyof typeof blank, v: string | boolean) => setF(p => ({ ...p, [k]: v }))

  const save = async () => {
    if (!f.name.trim()) { toast.error('Vendor name is required'); return }
    setSaving(true)
    try {
      const payload = { ...f }
      if (vendor) await updateVendor(vendor.id, payload as Partial<Vendor>)
      else await createVendor(payload as Partial<Vendor>)
      toast.success('Vendor saved!'); onSaved()
    } catch (e) { console.error(e); toast.error('Failed to save vendor') } finally { setSaving(false) }
  }

  const F = (label: string, k: keyof typeof blank, cls = '') => (
    <div className={cls}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input value={f[k] as string} onChange={e => set(k, e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{vendor ? 'Edit Vendor' : 'New Vendor'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-3">
          {F('Name', 'name', 'col-span-2 md:col-span-2')}
          {F('Vendor Code', 'vendor_code')}
          {F('Phone', 'phone')}
          {F('Mobile', 'mobile')}
          {F('Email', 'email')}
          {F('Address', 'address', 'col-span-2 md:col-span-3')}
          {F('City', 'city')}
          {F('State', 'state')}
          {F('Zip', 'zip')}
          {F('Website', 'website')}
          {F('Terms', 'terms')}
          <label className="flex items-center gap-2 mt-6 text-sm text-gray-700">
            <input type="checkbox" checked={f.is_active} onChange={e => set('is_active', e.target.checked)} /> Active
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  )
}
